import React, { useEffect, useMemo, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";
import pluralize from "pluralize";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  RiCheckboxCircleLine,
  RiCloseLine,
  RiErrorWarningLine,
} from "@remixicon/react";
import { useTheme } from "@mui/material/styles";
import {
  format,
  parseISO,
  formatISO,
  isBefore,
  isEqual,
  startOfDay,
  addMonths,
  addDays,
} from "date-fns";

import { DeliveryItemT, DialogTypesT } from "../../../types/ScheduleForm";
import { AuthContextType, useAuth } from "../../../contexts/AuthContext";
import {
  generate_schedule,
  translate_cron,
  useCommitSchedule,
} from "../../../services/scheduling";
import { ScheduleDialogProps } from "../../../types/ScheduleForm";
import { SchedulingEvent_T } from "../../../types/Scheduling";
import RecurringScheduledEvents from "./RecurringScheduledEvents";
import RecurringSummaryCounts from "./RecurringSummaryCounts";
import DeliveriesCombo from "./DeliveriesCombo";
import { generateFormLabelTypographyProps } from "./FormLabelProps";

const CRON_OPTIONS = [
  { value: "0 0 * * 4", label: "At 12:00 AM, only on Thursday" },
  {
    value: "0 0 * * 4#1",
    label: "At 12:00 AM, on the first Thursday of the month",
  },
  {
    value: "0 0 * * 4L",
    label: "At 12:00 AM, on the last Thursday of the month",
  },
  {
    value: "0 0 * */3 4L",
    label: "At 12:00 AM, on the last Thursday of the month, every 3 months",
  },
  {
    value: "0 0 * */2 4#1",
    label: "At 12:00 AM, on the first Thursday of the month, every 2 months",
  },
];

const RecurringDialog: React.FC<ScheduleDialogProps> = ({
  derivedStartDate,
  derivedEndDate,
  selectedRows = [],
  action = "ADD",
  onClose,
  onSuccessCallback,
}) => {
  const userAuth: AuthContextType = useAuth();
  const { removeAndAddSchedule } = useCommitSchedule();
  const theme = useTheme();

  const [startDate, setStartDate] = useState<string>(
    derivedStartDate ? derivedStartDate : format(new Date(), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState<string>(
    derivedEndDate
      ? derivedEndDate
      : format(addMonths(new Date(), 1), "yyyy-MM-dd")
  );

  const [dateError, setDateError] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] =
    useState<DeliveryItemT[]>(selectedRows);
  const [addAnother, setAddAnother] = useState<boolean>(false);
  const [offScheduleDate, setOffScheduleDate] = useState<string | null>(null);
  const [volumeUpgrade, setVolumeUpgrade] = useState<number>(0);

  const [cronInput, setCronInput] = useState<string>(CRON_OPTIONS[0].value);
  const [cronDescription, setCronDescription] = useState<string>("");
  const [isCronValid, setIsCronValid] = useState<boolean>(true);
  const [cronError, setCronError] = useState<string>("");
  const [generatedSchedule, setGeneratedSchedule] = useState<
    SchedulingEvent_T[]
  >([]);
  const [addRemoveStatus, setAddRemoveStatus] = useState("");

  const formLabelTypographyProps = generateFormLabelTypographyProps(theme);

  const validateCron = async (cron: string) => {
    if (!cron || cron.length < 5) {
      setCronError("");
      setIsCronValid(false);
      return;
    }

    try {
      const cronDescription = await translate_cron(cron.split("-")[0].trim());
      setCronDescription(cronDescription);
      setCronError("");
      setIsCronValid(true);
    } catch (err) {
      setCronDescription("");
      setCronError("Invalid cron syntax");
      setIsCronValid(false);
    }
  };

  const debouncedValidateCron = useDebounceCallback((value: string) => {
    validateCron(value);
  }, 300);

  const isValidForm = useMemo(() => {
    const startValid =
      startDate && endDate && new Date(startDate) < new Date(endDate);

    return startValid && isCronValid && selectedItems.length > 0;
  }, [startDate, endDate, isCronValid, selectedItems]);

  const generateSchedule = async () => {
    try {
      const isoStartDate = format(parseISO(startDate), "yyyy-MM-dd'T'00:00:00");
      const isoEndDate = format(parseISO(endDate), "yyyy-MM-dd'T'00:00:00");

      const isoOffScheduleDate = offScheduleDate
        ? format(parseISO(offScheduleDate), "yyyy-MM-dd'T'00:00:00")
        : "";

      const schedule = await generate_schedule(
        isoStartDate,
        isoEndDate,
        cronInput,
        isoOffScheduleDate,
        volumeUpgrade
      );

      setGeneratedSchedule(schedule);
    } catch (err) {
      console.error("Error generating schedule:", err);
    }
  };

  useEffect(() => {
    if (!isValidForm) {
      setGeneratedSchedule([]);
    } else {
      generateSchedule();
    }
  }, [
    startDate,
    endDate,
    isValidForm,
    volumeUpgrade,
    offScheduleDate,
    cronInput,
  ]);

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, id } = event.target;

    if (id === "startDate") {
      setStartDate(value);
    } else if (id === "endDate") {
      setEndDate(value);
    } else if (id === "offScheduleDate") {
      setOffScheduleDate(value);
    }
  };

  const validateDateRange = () => {
    const start = startOfDay(parseISO(startDate));
    const end = startOfDay(parseISO(endDate));

    if (isBefore(start, end) || isEqual(start, end)) {
      setDateError(false);
    } else {
      setDateError(true);
    }
  };

  const handleSubmit = async () => {
    if (dateError || !isValidForm) return;

    const formattedStartDate = formatISO(parseISO(startDate), {
      representation: "date",
    });

    const formattedEndDate = formatISO(parseISO(endDate), {
      representation: "date",
    });

    const formattedOffScheduleDate = offScheduleDate
      ? formatISO(parseISO(offScheduleDate), { representation: "date" })
      : null;

    const dateStart =
      formattedOffScheduleDate &&
      isBefore(
        startOfDay(parseISO(formattedOffScheduleDate)),
        startOfDay(parseISO(formattedStartDate))
      )
        ? formattedOffScheduleDate
        : formattedStartDate;

    const payload = {
      overwrite_delivery_ids: selectedItems.map((item) => item.delivery_id),
      date_start: dateStart,
      date_end: formattedEndDate,
      new_schedule: generatedSchedule,
      admin_id: userAuth.authAttributes?.id ?? "",
    };

    try {
      const result = await removeAndAddSchedule(
        payload.overwrite_delivery_ids,
        payload.date_start,
        payload.date_end,
        payload.new_schedule,
        payload.admin_id.toString(),
        setAddRemoveStatus
      );

      if (result.success) {
        if (addAnother) {
          setTimeout(() => {
            setSelectedItems([]);
            setAddRemoveStatus("");
          }, 5000);
        } else {
          onSuccessCallback?.();
          onClose();
        }
      } else {
        console.error("Failed to commit schedule");
      }
    } catch (error) {
      console.error("Error committing schedule:", error);
      setAddRemoveStatus("Could not commit schedule.");
    }
  };

  const renderSubmitButtonLabel = () => {
    const isSubmitting =
      addRemoveStatus.length > 0 &&
      !addRemoveStatus.toLowerCase().includes("error");

    if (action === DialogTypesT.CHANGE) {
      if (isSubmitting) {
        return "Changing";
      }
      return "Change";
    }

    if (isSubmitting) {
      return "Adding";
    }
    return "Add";
  };

  useEffect(() => {
    validateCron(cronInput);
  }, []);

  useEffect(() => {
    validateDateRange();
  }, [startDate, endDate]);

  return (
    <>
      <DialogTitle>
        {action === DialogTypesT.ADD ? "Add" : "Change"} recurring deliveries
        <IconButton
          aria-label="close"
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <RiCloseLine />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "24px",
            alignItems: "start",
            width: "100%",
            margin: "0 auto",
          }}
        >
          <FormLabel
            {...formLabelTypographyProps}
            sx={{ ...formLabelTypographyProps.sx, mt: 1 }}
          >
            Deliveries *
          </FormLabel>
          <DeliveriesCombo
            selectedItems={selectedItems}
            onSelectionChange={setSelectedItems}
            action={action}
          />
          <FormLabel htmlFor="startDate" {...formLabelTypographyProps}>
            Start Date *
          </FormLabel>
          <TextField
            id="startDate"
            type="date"
            size="small"
            fullWidth
            value={startDate}
            onChange={handleDateChange}
            error={dateError}
            helperText={dateError ? "Start date must be before end date." : ""}
            inputProps={{
              min: format(new Date(), "yyyy-MM-dd"), // minimum date is today
            }}
          />
          <FormLabel htmlFor="endDate" {...formLabelTypographyProps}>
            End Date *
          </FormLabel>
          <TextField
            id="endDate"
            type="date"
            size="small"
            fullWidth
            value={endDate}
            onChange={handleDateChange}
            error={dateError}
            helperText={dateError ? "End date must be after start date." : ""}
            inputProps={{
              min: format(addDays(new Date(), 1), "yyyy-MM-dd"), // minimum date is today + 1
            }}
          />
          {action === DialogTypesT.CHANGE && (
            <Box gridColumn="2 / -1" sx={{ mt: 2 }}>
              <Alert severity="info" icon={<RiErrorWarningLine />}>
                Note: All fields below have been reset to default values.
              </Alert>
            </Box>
          )}
          <FormLabel htmlFor="frequency" {...formLabelTypographyProps}>
            Frequency (CRON) *
          </FormLabel>
          <Box
            role="radiogroup"
            aria-label="CRON frequency options"
            sx={{ margin: 0 }}
          >
            {CRON_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                aria-describedby={`cron-description-${option.value}`}
                control={
                  <Radio
                    checked={cronInput === option.value}
                    onChange={() => {
                      setCronInput(option.value);
                      debouncedValidateCron(option.value);
                    }}
                    size="small"
                  />
                }
                label={
                  <Tooltip title={option.value} arrow placement="top">
                    <Typography
                      variant="body2"
                      id={`cron-description-${option.value}`}
                      sx={{ fontSize: "0.875rem", color: "gray.300" }}
                    >
                      {option.label}
                    </Typography>
                  </Tooltip>
                }
                sx={{ margin: 0 }}
              />
            ))}
            <TextField
              placeholder="Enter custom CRON"
              variant="outlined"
              size="small"
              fullWidth
              value={cronInput}
              sx={{ marginTop: 1 }}
              onChange={(e) => {
                const { value } = e.target;
                setCronInput(value);
                debouncedValidateCron(value);
              }}
            />
            {cronDescription &&
              isCronValid &&
              !CRON_OPTIONS.some((option) => option.value === cronInput) && (
                <Typography
                  color="success.main"
                  sx={{ fontSize: "0.875rem", marginTop: 1 }}
                >
                  <Box display="flex" alignItems="center">
                    <RiCheckboxCircleLine
                      style={{ marginRight: 4 }}
                      size={16}
                    />
                    <Box>{cronDescription}</Box>
                  </Box>
                </Typography>
              )}
            {!!cronError && (
              <Typography
                variant="body2"
                color="error.main"
                sx={{ fontSize: "0.875rem", marginTop: 1 }}
              >
                <Box display="flex" alignItems="center">
                  <RiErrorWarningLine style={{ marginRight: 4 }} size={16} />
                  <Box>{cronError}</Box>
                </Box>
              </Typography>
            )}
          </Box>
          <FormLabel htmlFor="offScheduleDate" {...formLabelTypographyProps}>
            Off-schedule first delivery
          </FormLabel>
          <TextField
            id="offScheduleDate"
            type="date"
            size="small"
            fullWidth
            value={offScheduleDate || ""}
            onChange={handleDateChange}
            inputProps={{
              min: format(new Date(), "yyyy-MM-dd"),
            }}
          />
          <FormLabel component="legend" {...formLabelTypographyProps}>
            Volume Upgrade
          </FormLabel>
          <Box display="flex" alignItems="center">
            <Typography variant="body2" sx={{ marginRight: 1 }}>
              Every
            </Typography>
            <TextField
              type="number"
              size="small"
              value={volumeUpgrade === 0 ? "0" : volumeUpgrade.toString()}
              onChange={(e) => {
                const value = e.target.value;
                // allows empty input to clear the field; only allows numbers
                if (value === "" || /^\d+$/.test(value)) {
                  setVolumeUpgrade(value === "" ? 0 : Number(value));
                }
              }}
              inputProps={{
                min: 0,
                step: 1,
              }}
              sx={{ width: "80px", marginRight: 1 }}
            />
            <Typography variant="body2">
              {pluralize("fulfillment", volumeUpgrade)}
            </Typography>
          </Box>
          <FormLabel component="legend" {...formLabelTypographyProps}>
            Generated Schedule
          </FormLabel>
          <RecurringScheduledEvents
            isFormValid={!!isValidForm}
            generatedSchedule={generatedSchedule}
          />
          <FormLabel component="legend" {...formLabelTypographyProps}>
            Summary Counts
          </FormLabel>
          <Box>
            <RecurringSummaryCounts
              startDate={startDate}
              endDate={endDate}
              selectedItems={selectedItems.map((item) => item.delivery_id)}
            />
            {addRemoveStatus.length > 0 && (
              <Alert
                severity={
                  addRemoveStatus.toLocaleLowerCase().includes("error")
                    ? "error"
                    : "success"
                }
                icon={false}
                sx={{
                  mt: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {addRemoveStatus.toLowerCase().includes("error") ? (
                    <RiErrorWarningLine
                      size={20}
                      style={{
                        color: theme.palette.error.main,
                        marginRight: 8,
                      }}
                    />
                  ) : addRemoveStatus.toLowerCase().includes("success") ? (
                    <RiCheckboxCircleLine
                      size={20}
                      style={{
                        color: theme.palette.success.main,
                        marginRight: 8,
                      }}
                    />
                  ) : (
                    <CircularProgress
                      size={20}
                      style={{
                        color: theme.palette.success.main,
                        marginRight: 8,
                      }}
                    />
                  )}
                  <Typography variant="body2" sx={{ lineHeight: "normal" }}>
                    {addRemoveStatus}
                  </Typography>
                </Box>
              </Alert>
            )}
          </Box>
          <DialogActions
            style={{
              gridColumn: "1 / -1",
              justifyContent: "end",
              marginTop: "16px",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={addAnother}
                  onChange={(e) => setAddAnother(e.target.checked)}
                  name="addAnother"
                  size="small"
                />
              }
              label="Create more"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              sx={{ minWidth: "120px" }}
              disabled={
                !isValidForm ||
                (addRemoveStatus.length > 0 &&
                  !addRemoveStatus.toLowerCase().includes("error"))
              }
            >
              {renderSubmitButtonLabel()}{" "}
              {pluralize("delivery", selectedItems.length)}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </>
  );
};

export default RecurringDialog;
